import { useEffect, useRef, useState } from 'react';
import { useTenant } from '../../context/TenantContext.jsx';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { saveInlineBlock, formatContentErrorMessage } from '../../lib/content.js';
import { RotateCcw, Check, AlertTriangle, AlertCircle } from 'lucide-react';

/**
 * EditableText — inline editierbarer Text im Storefront (Issue #2, Paket C).
 *
 * WordPress-Prinzip: Der Kunde bleibt im Shop. Ohne Bearbeiten-Modus verhält
 * sich die Komponente wie ein normaler Text (kein Overlay, kein chrome).
 * Im Bearbeiten-Modus (nur tenant_admin des eigenen Mandanten) zeigt sie eine
 * gestrichelte Umrandung; Klick -> Inline-Input; Enter/Blur speichert den
 * CMS-Block per Upsert (einzelner Block — fehlende Blöcke bleiben unangetastet).
 * Leer speichern oder "Auf Standard zurücksetzen" setzt den Wert auf Standardtext (Fallback).
 *
 * @param {string} page       - CMS-Seite ('home', 'catalog', 'product', 'footer', 'legal', 'brand', 'onboarding')
 * @param {string} blockKey   - CMS-Block-Schlüssel (z. B. 'hero_title', 'footer_description', 'imprint_content')
 * @param {string} value      - aktuell angezeigter Text (Override oder Fallback)
 * @param {boolean} [multiline] - mehrzeiliges Eingabefeld (textarea) für längere Beschreibungen/Rechtstexte
 */
export function EditableText({ page, blockKey, value, children, className = '', tag: Tag = 'span', multiline = false, ...props }) {
  // useTenant try/catch: isoliertes Rendering (Tests) ohne TenantProvider.
  let tenant = null;
  try {
    tenant = useTenant()?.tenant || null;
  } catch {
    tenant = null;
  }
  const {
    canEdit,
    editMode,
    bumpRevision,
    sessionExpired,
    recheckSession,
    notifySessionExpired,
    setUnsavedEdit,
  } = useEditMode();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  const inputRef = useRef(null);
  const editKey = `${page || 'page'}:${blockKey || 'block'}`;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  if (!canEdit || !editMode) {
    return <Tag className={className} {...props}>{children ?? value}</Tag>;
  }

  const beginEdit = () => {
    setDraft(value || '');
    setError('');
    setConfirmReset(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setConfirmReset(false);
    setEditing(false);
    setError('');
    setDraft(value || '');
    setUnsavedEdit?.(editKey, false);
  };

  const handleDraftChange = (newVal) => {
    setDraft(newVal);
    setError('');
    setUnsavedEdit?.(editKey, newVal !== (value || ''));
  };

  const commit = async () => {
    const next = draft.trim();
    if (next === (value || '').trim()) {
      setEditing(false);
      setConfirmReset(false);
      setUnsavedEdit?.(editKey, false);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await saveInlineBlock(tenant?.id, page, {
        block_key: blockKey,
        type: 'text',
        value: next,
        sort: 0,
      }, null); // null = Session-Cookie (tenant_admin), kein API-Key
      setEditing(false);
      setConfirmReset(false);
      setUnsavedEdit?.(editKey, false);
      bumpRevision();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      const msg = formatContentErrorMessage(err);
      setError(msg);
      if (err?.status === 401 || err?.statusCode === 401) {
        notifySessionExpired?.(true);
        recheckSession?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const resetToStandard = async () => {
    setConfirmReset(false);
    setSaving(true);
    setError('');
    try {
      await saveInlineBlock(tenant?.id, page, {
        block_key: blockKey,
        type: 'text',
        value: '',
        sort: 0,
      }, null);
      setEditing(false);
      setUnsavedEdit?.(editKey, false);
      bumpRevision();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (err) {
      const msg = formatContentErrorMessage(err);
      setError(msg);
      if (err?.status === 401 || err?.statusCode === 401) {
        notifySessionExpired?.(true);
        recheckSession?.();
      }
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (multiline) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    } else {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    }
  };

  return (
    <Tag className={`relative inline-block group ${className}`} {...props}>
      <span
        role="button"
        tabIndex={0}
        onClick={beginEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') beginEdit();
        }}
        title="Zum Bearbeiten klicken"
        className={`cursor-text rounded-sm outline-dashed outline-1 outline-offset-2 transition-colors hover:bg-[var(--primary)]/5 ${
          savedFlash ? 'outline-emerald-500 bg-emerald-50 text-emerald-900 font-medium' : 'outline-slate-400/70'
        } ${saving ? 'opacity-50' : ''}`}
      >
        {children ?? value}
        {saving && <span className="ml-2 text-xs text-slate-500 font-normal">speichert…</span>}
        {savedFlash && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-700 font-bold" data-testid="save-success-feedback">
            <Check className="h-3.5 w-3.5 text-emerald-600" /> Gespeichert
          </span>
        )}
      </span>

      {/* Screen Reader Live Announcements */}
      {savedFlash && (
        <span role="status" aria-live="polite" className="sr-only">
          Gespeichert.
        </span>
      )}

      {editing && (
        <span className={`absolute left-0 top-full z-50 mt-1 flex ${multiline ? 'w-full min-w-[340px] max-w-lg flex-col' : 'w-full min-w-[300px]'} items-stretch gap-1.5 rounded-lg border border-[var(--primary)]/40 bg-white p-2.5 shadow-xl`}>
          {sessionExpired && (
            <div
              role="alert"
              data-testid="session-expired-inline-alert"
              className="flex items-center gap-1.5 rounded-md bg-amber-50 p-2 text-xs text-amber-800 border border-amber-300 font-medium"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <span>Sitzung ist abgelaufen. Bitte vor dem Speichern neu anmelden.</span>
            </div>
          )}

          {multiline ? (
            <textarea
              ref={inputRef}
              rows={5}
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={saving}
              className="w-full rounded-md border border-[var(--border)] p-2 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] resize-y"
              placeholder="Leer lassen = Standardtext"
            />
          ) : (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={saving}
              className="w-full rounded-md border border-[var(--border)] px-2 py-1.5 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)]"
              placeholder="Leer lassen = Standardtext"
            />
          )}

          {confirmReset ? (
            <div className="flex items-center justify-between gap-1 mt-1.5 pt-1.5 border-t border-slate-100 bg-amber-50/80 -mx-1 px-2 py-1 rounded text-xs text-amber-900" data-testid="editable-text-reset-confirm-box">
              <span className="font-medium text-xs">Wirklich auf Standard zurücksetzen?</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={saving}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={resetToStandard}
                  data-testid="editable-text-reset-confirm"
                  className="rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-red-700 cursor-pointer"
                >
                  Ja, zurücksetzen
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setConfirmReset(false)}
                  data-testid="editable-text-reset-cancel"
                  className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100 cursor-pointer"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-1 mt-1.5 pt-1 border-t border-slate-100">
              <button
                type="button"
                disabled={saving}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setConfirmReset(true)}
                data-testid="editable-text-reset"
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 cursor-pointer transition-colors"
                title="Diesen Text auf den Standardtext zurücksetzen"
              >
                <RotateCcw className="h-3 w-3" />
                Standard
              </button>

              <div className="flex items-center gap-1">
                {multiline && (
                  <span className="text-xs text-slate-400 mr-1">
                    Strg+Enter zum Speichern
                  </span>
                )}
                <button
                  type="button"
                  disabled={saving}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={commit}
                  className="shrink-0 rounded-md bg-[var(--primary)] px-2.5 py-1 text-xs font-bold text-white cursor-pointer hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? 'Speichert…' : 'OK'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelEdit}
                  className="shrink-0 rounded-md border border-[var(--border)] px-2.5 py-1 text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-50"
                  title="Schließen"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="mt-1 flex items-start gap-1.5 rounded-md bg-red-50 p-2 text-xs text-red-700 border border-red-200"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </span>
      )}
    </Tag>
  );
}